import {TinyEmitter} from 'tiny-emitter';
import * as Vue from 'vue';
import {type App, type ComponentPublicInstance} from 'vue';
import AppPopup from '../components/AppPopup.vue';
import {SubmitIcons} from './design';
import {createChildApp, createComponent, NoOperation} from './utils';

let vueApp: Vue.App<Element>;

export const popupDefaultCloseButton: PopupButton = {
    type: 'default',
    title: 'Schließen',
    icon: SubmitIcons.close,
    cmd(popup, event)
    {
        popup.close(event);
    },
};

export const popupDefaultCancelButton: PopupButton = {
    type: 'default',
    title: 'Abbrechen',
    icon: SubmitIcons.close,
    cmd(popup, event)
    {
        popup.close(event, 'aborted');
    },
};

export let popUpInstances: { id: string, popup: IAppPopupContext }[] = [];

export const getCountOfAllOpenPopups = () =>
{
    return popUpInstances.filter(popupInstance => popupInstance.popup.isOpen.value).length;
};

export function setPopupVueApp(app: Vue.App<Element>)
{
    vueApp = app;
}

export function closeAllPopUps()
{
    for (const popUpInstance of Array.from(popUpInstances))
    {
        popUpInstance.popup && !popUpInstance.popup.persistent && popUpInstance.popup.close();
    }
}

// @deprecated, use Store#createPopup
export class Popup
{
    private _App: Nullable<App<Element>>;

    private _Instance: Nullable<ComponentPublicInstance<IAppPopupContext>>;

    private _Element: Nullable<Element>;

    private _SubComponentApp: Nullable<App<Element>>;

    private _SubComponentElement: Nullable<Element>;

    constructor(options: PopupOptions)
    {
        // temporary fix wrongly implemented close events
        if (options && options.events && options.events.close)
        {
            if (process.env.NODE_ENV === 'development')
            {
                console.error('don\'t use options.events.close -> change to options.onClose');
            }

            options.onClose = options.events.close;
        }

        options.persistent = true === options.persistent;

        this._Element = document.createElement('div');
        document.body.appendChild(this._Element);

        // only pass existing props as componentOptions, non-existing props will be handled as attributes and attributes are not supported on <teleport> causing warnings
        const componentOptions = Object.assign({}, options);
        delete componentOptions.component;
        delete componentOptions.componentProps;
        delete componentOptions.events;

        const {
                  app: childApp,
                  component,
              } = createComponent({
            app: vueApp,
            component: AppPopup,
            props: componentOptions,
            el: this._Element,
        });

        this._App      = childApp;
        this._Instance = component as ComponentPublicInstance<IAppPopupContext>;

        this._Instance!.emitter.on('open', () => this.open());
        this._Instance!.emitter.on('close', () => this.destroy());
        this._Instance!.emitter.on('scrollTop', () => this.scrollTop());

        if (options.component)
        {
            this._Instance!.$nextTick(() =>
            {
                this._SubComponentElement = document.createElement('div');
                // @ts-ignore
                const subComponentWrapper = document.querySelector(`#${this._Instance.descriptionElementId}`);
                subComponentWrapper && subComponentWrapper.appendChild(this._SubComponentElement);

                const emitter = new TinyEmitter();

                const subComponentEvents = Object.assign(
                    {},
                    options.events || {},
                    {
                        close: () =>
                        {
                            this.close();
                        },
                        setButtons: (buttons: PopupButton[]) =>
                        {
                            if (this._Instance)
                            {
                                this._Instance.overwriteOptions({
                                    '_buttons': buttons,
                                });
                            }
                        },
                        setTitle: (title: string) =>
                        {
                            if (this._Instance)
                            {
                                this._Instance.overwriteOptions({
                                    '_title': title,
                                });
                            }
                        },
                        setSize: (size: PopupSize) =>
                        {
                            if (this._Instance)
                            {
                                this._Instance.overwriteOptions({
                                    '_size': size,
                                });
                            }
                        },
                        setData: (options: IAppPopupOverwriteAbleOptions) =>
                        {
                            if (this._Instance)
                            {
                                this._Instance.overwriteOptions(options);
                            }
                        },
                        emit: (emitData: AppPopupEmit) =>
                        {
                            if (this._Instance)
                            {
                                this._Instance.emitter.emit(emitData.event, emitData.data);
                            }
                        },
                        'lazy-load-focus': () =>
                        {
                            if (this._Instance)
                            {
                                this._Instance!.$nextTick(() =>
                                {
                                    this._Instance!.queryFocusableElements();
                                    this._Instance!.focusFirstElement();
                                });
                            }
                        },
                    },
                );

                for (let event in subComponentEvents)
                {
                    if (subComponentEvents.hasOwnProperty(event))
                    {
                        emitter.on(event, subComponentEvents[event]);
                    }
                }

                // class/options component
                if (options.component.data)
                {
                    const subComponentData = options.component.data();
                    options.component.data = () =>
                    {
                        return Object.assign(subComponentData, {emitter});
                    };
                }

                const SubComponentApp     = createChildApp({
                    app: vueApp,
                    component: options.component,
                    props: options.componentProps ?? {},
                });
                const SubComponentEmitter = new TinyEmitter();

                // setup component
                if (!options.component.data)
                {
                    for (let event in subComponentEvents)
                    {
                        if (subComponentEvents.hasOwnProperty(event))
                        {
                            SubComponentEmitter.on(event, subComponentEvents[event]);
                        }
                    }
                }

                SubComponentApp.provide('popup-emitter', SubComponentEmitter);
                SubComponentApp.mount(this._SubComponentElement);

                this._SubComponentApp = SubComponentApp;
            }).then(NoOperation);
        }
    }

    get Instance(): ComponentPublicInstance
    {
        if (null === this._Instance)
        {
            throw new Error('Popup-Instanz nicht gefunden.');
        }

        return this._Instance;
    }

    open()
    {
        return this._Instance?.open();
    }

    close()
    {
        return this._Instance?.close();
    }

    destroy()
    {
        if (this._App)
        {
            if (this._SubComponentApp)
            {
                this._SubComponentApp.unmount();
                this._SubComponentElement && this._SubComponentElement.remove();
                this._SubComponentApp     = null;
                this._SubComponentElement = null;
            }

            this._App.unmount();
            this._Element && this._Element.remove();
            this._App      = null;
            this._Instance = null;
            this._Element  = null;
        }
    }

    scrollTop()
    {
        return this._Instance?.scrollTop();
    }

    scrollBottom()
    {
        return this._Instance?.scrollBottom();
    }
}